<template>
  <div class="i-action-card">
    <div>
      <img
        v-if="props.imageUrl"
        :src="props.imageUrl"
      />
      <!-- For IOS we need to add playsinline attribute -->
      <video
        v-if="!props.imageUrl"
        autoplay
        muted
        loop
        playsinline
      >
        <source
          :src="props.videoUrl"
          type="video/mp4"
        />
      </video>
    </div>

    <div class="wrap">
      <div
        class="texts"
        :style="{ '--lg-width': props.ctaText ? 'fit-content' : '75%' }"
      >
        <div
          v-if="props.caption"
          class="subtitle"
        >
          {{ props.caption }}
        </div>

        <div
          v-if="props.text"
          class="title"
          v-html="props.text"
        ></div>
      </div>

      <div
        v-if="props.ctaText || isDesktop"
        class="cta"
      >
        <iBaseButton
          v-if="props.ctaText"
          :pre-icon="props.preIcon"
          :background-color="azureRadiance700"
          border-radius="8px"
          font-weight="500"
          size="small"
          :text="props.ctaText"
          text-color="white"
          width="100%"
          @click="$emit('ctaClicked')"
        />
      </div>
    </div>

    <div class="overlay"></div>
  </div>
</template>

<script setup lang="ts">
import { azureRadiance700 } from "@/styles/colors"
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseButton from "@/components/base/BaseButton.vue"

// COMPOSABLES
const isDesktop = useIsScreenSize(breakpointMd)

// EMITS
defineEmits(["ctaClicked"])

const props = defineProps({
  text: {
    type: String,
    default: ""
  },
  caption: {
    type: String,
    default: ""
  },
  ctaText: {
    type: String,
    default: ""
  },
  imageUrl: {
    type: String,
    default: ""
  },
  videoUrl: {
    type: String,
    default: "https://jobtestorg-misc.s3.us-east-2.amazonaws.com/app-assets/jobtest-career-coach-video-1.mp4"
  },
  preIcon: {
    type: String
  }
})
</script>

<style lang="scss" scoped>
.i-action-card {
  @include border-radius-lg;

  align-items: flex-end;
  display: flex;
  height: 374px;
  overflow: hidden;
  position: relative;
  color: white;
  text-align: left;

  @media screen and (min-width: $breakpoint-md) {
    height: 320px;
  }

  .overlay {
    background: linear-gradient(180deg, rgb(24 34 48 / 0%) 0%, rgb(13 18 25 / 50%) 48.44%, rgb(0 0 0 / 60%) 100%);
    height: 50%;
    width: 100%;
  }

  .wrap {
    overflow: hidden;
    position: absolute;
    width: 100%;
    padding: 32px 24px;

    @include flex($direction: column, $justify: center, $align: start, $gap: 8px);

    @media screen and (min-width: $breakpoint-md) {
      padding: 30px 32px;

      @include flex($direction: row, $justify: space-between, $align: end, $gap: 0);
    }

    .cta {
      width: 100%;
      margin-top: 6px;

      @media screen and (min-width: $breakpoint-sm) {
        width: 180px;
        flex: 0 0 180px;
      }
    }

    .texts {
      width: 100%;

      @media screen and (min-width: $breakpoint-md) {
        width: unset;
      }

      @media screen and (min-width: $breakpoint-lg) {
        width: var(--lg-width);
      }

      .subtitle {
        @include text-caption;

        font-weight: 700;
        margin-bottom: 6px;
      }

      .title {
        font-size: 24px;
        line-height: 31px;
        font-weight: 500;
      }
    }
  }

  video,
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>

<style lang="scss">
.i-action-card {
  .highlighted {
    background-color: $azure-radiance500;
    padding: 0 6px;
    color: $white;

    @include border-radius-sm;

    white-space: nowrap;
  }
}
</style>
